import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { api, SettingsData, getAccountId } from '../services/api';
import { logger } from '../utils/logger';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { cn } from '@/lib/utils';
import { ActivityLog } from './ActivityLog';

interface FormData {
  customerSearchType: 'internal' | 'external';
  numberLength: number;
  defaultExpireDays: number;
  giftCardProductName: string;
}

function SettingsForm() {
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [isActivated, setIsActivated] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      customerSearchType: 'internal',
      numberLength: 8,
      defaultExpireDays: 0, // 0 = не ограничен
      giftCardProductName: 'Подарочный сертификат',
    },
    mode: 'onChange', // Валидация в реальном времени
  });

  const loadSettings = useCallback(async (accountIdToUse?: string) => {
    try {
      // Используем переданный accountId или из state
      const id = accountIdToUse || accountId;
      if (!id) {
        logger.warn('AccountId not available, cannot load settings');
        return;
      }
      
      // Передаём accountId напрямую, чтобы избежать повторного вызова getAccountId()
      const settings = await api.getSettings(id);
      
      // Проверяем, активировано ли решение (есть ли baseUrl в настройках)
      const activated = !!(settings.baseUrl);
      setIsActivated(activated);
      
      // baseUrl и authToken устанавливаются автоматически, не загружаем из настроек
      if (settings.customerSearchType) {
        setValue('customerSearchType', settings.customerSearchType);
      }
      if (settings.giftCardSettings) {
        if (settings.giftCardSettings.numberLength) {
          setValue('numberLength', settings.giftCardSettings.numberLength);
        }
        if (settings.giftCardSettings.defaultExpireDays) {
          setValue('defaultExpireDays', settings.giftCardSettings.defaultExpireDays);
        }
      }
      if (settings.giftCardProduct) {
        if (settings.giftCardProduct.productName) {
          setValue('giftCardProductName', settings.giftCardProduct.productName);
        }
      }
    } catch (err) {
      logger.error('Ошибка при загрузке настроек', err);
      // Если ошибка 404, значит решение не активировано
      setIsActivated(false);
    }
  }, [setValue, accountId]);

  useEffect(() => {
    // Пробуем получить accountId при загрузке
    const initAccountId = async () => {
      try {
        const id = await getAccountId();
        if (id) {
          setAccountId(id);
          await loadSettings(id);
        } else {
          // Убираем fallback на localStorage - это небезопасно при смене аккаунта
          logger.warn('AccountId not available, cannot load settings');
        }
      } catch (err) {
        logger.error('Ошибка при получении accountId', err);
      }
    };
    
    initAccountId();
  }, [loadSettings, setValue]);


  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);
    setSaved(false);

    try {
      // Используем accountId из state или пытаемся получить автоматически (fallback)
      let accountIdToUse = accountId;
      if (!accountIdToUse) {
        accountIdToUse = await getAccountId();
      }
      
      if (!accountIdToUse) {
        setError('Не удалось определить Account ID. Пожалуйста, обновите страницу.');
        setLoading(false);
        return;
      }

      // Сохраняем accountId в localStorage и state
      localStorage.setItem('accountId', accountIdToUse);
      setAccountId(accountIdToUse);

      // baseUrl должен включать путь к Loyalty API
      // МойСклад будет добавлять к baseUrl путь эндпоинта (например /giftcard)
      // Поэтому baseUrl должен быть: https://giftms.espotto.ru/api/moysklad/loyalty/1.0
      const baseUrl = `${window.location.origin}/api/moysklad/loyalty/1.0`;

      const settings: SettingsData = {
        baseUrl: baseUrl,
        // authToken не передаём, так как он генерируется автоматически на бэкенде
        customerSearchType: data.customerSearchType,
        giftCardSettings: {
          numberLength: data.numberLength,
          defaultExpireDays: data.defaultExpireDays,
        },
        giftCardProduct: {
          productName: data.giftCardProductName || undefined,
        },
      };

      // Используем accountId из state или полученный автоматически
      await api.saveSettingsWithAccountId(accountIdToUse, settings);
      setSaved(true);
      setIsActivated(true); // Решение активировано после успешного сохранения
      setTimeout(() => setSaved(false), 3000);
      
      // Уведомляем родительский компонент об активации или изменении настроек
      window.dispatchEvent(new CustomEvent('solutionActivated', { 
        detail: { customerSearchType: data.customerSearchType } 
      }));
    } catch (err: any) {
      logger.error('Ошибка при сохранении настроек', err);
      const errorMessage = 
        err.response?.data?.message || 
        err.response?.data?.error || 
        err.message || 
        'Ошибка при сохранении настроек';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg p-6 w-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-foreground m-0">Настройки программы лояльности</h1>
        {isActivated && (
          <Button onClick={() => setShowActivityLog(true)} variant="outline">
            Журнал действий
          </Button>
        )}
      </div>
      
      <div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="customerSearchType">Способ поиска покупателей</Label>
          <Select
            value={watch('customerSearchType')}
            onValueChange={(value) => setValue('customerSearchType', value as 'internal' | 'external')}
          >
            <SelectTrigger id="customerSearchType">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="internal">Внутренний (в МойСклад)</SelectItem>
              <SelectItem value="external">Внешний (в системе лояльности)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="numberLength">Длина номера сертификата</Label>
          <Input
            id="numberLength"
            type="number"
            min="4"
            max="12"
            {...register('numberLength', {
              required: 'Обязательное поле',
              min: { value: 4, message: 'Минимум 4 символа' },
              max: { value: 12, message: 'Максимум 12 символов' },
              valueAsNumber: true,
            })}
            className={cn(errors.numberLength && "border-destructive")}
          />
          {errors.numberLength && (
            <p className="text-sm text-destructive">{errors.numberLength.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="defaultExpireDays">Срок действия по умолчанию (дней)</Label>
          <Input
            id="defaultExpireDays"
            type="number"
            min="0"
            max="3650"
            {...register('defaultExpireDays', {
              required: 'Обязательное поле',
              min: { value: 0, message: 'Минимум 0 дней (0 = не ограничен)' },
              max: { value: 3650, message: 'Максимум 3650 дней' },
              valueAsNumber: true,
            })}
            className={cn(errors.defaultExpireDays && "border-destructive")}
          />
          {errors.defaultExpireDays && (
            <p className="text-sm text-destructive">{errors.defaultExpireDays.message}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Укажите 0 для неограниченного срока действия
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="giftCardProductName">Название товара *</Label>
          <Input
            id="giftCardProductName"
            type="text"
            placeholder="Подарочный сертификат"
            {...register('giftCardProductName', {
              required: 'Название товара обязательно',
              maxLength: { value: 255, message: 'Максимум 255 символов' },
            })}
            className={cn(errors.giftCardProductName && "border-destructive")}
          />
          {errors.giftCardProductName && (
            <p className="text-sm text-destructive">{errors.giftCardProductName.message}</p>
          )}
          <p className="text-xs text-muted-foreground">Точное название товара из МойСклад</p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {saved && (
          <Alert variant="success">
            <AlertDescription>Настройки сохранены!</AlertDescription>
          </Alert>
        )}

            <Button type="submit" disabled={loading} className="w-full">
              {loading 
                ? 'Сохранение...' 
                : isActivated 
                  ? 'Сохранить изменения' 
                  : 'Активировать решение'}
            </Button>
          </form>
      </div>

      {/* Модальное окно журнала действий */}
      <Dialog open={showActivityLog} onOpenChange={setShowActivityLog}>
        <DialogContent 
          className="max-w-[1400px] max-h-[80vh] flex flex-col"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader className="pb-4">
            <DialogTitle>Журнал действий</DialogTitle>
            <DialogDescription>
              Полная история всех действий с сертификатами
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden min-h-0 -mx-6 px-6 -mt-2 pt-2">
            <ActivityLog />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SettingsForm;

